/**
 * Firm-wide discovery chase lists.
 *
 * Overdue acknowledgments: courts that have not answered a recorded request inside five business
 * days. Expert designation: cases with a trial date whose twenty-day window is open or already past.
 */

export interface OverdueAcknowledgmentRow {
  ticketGuid: string;
  citationNumber: string | null;
  causeNumber: string | null;
  clientName: string;
  courtName: string | null;
  requestedAt: string;
  deadline: string;
  businessDaysOverdue: number;
}

export interface OverdueAcknowledgmentsDto {
  asOf: string;
  businessDayWindow: number;
  awaitingAnswer: number;
  casesWithRequestDate: number;
  overdue: OverdueAcknowledgmentRow[];
}

export interface ExpertDesignationHeadsUpRow {
  ticketGuid: string;
  citationNumber: string | null;
  causeNumber: string | null;
  clientName: string;
  courtName: string | null;
  trialDate: string;
  designationDeadline: string;
  daysUntilTrial: number;
  daysUntilDeadline: number;
  deadlinePassed: boolean;
}

export interface ExpertDesignationHeadsUpDto {
  asOf: string;
  leadDays: number;
  daysBeforeTrial: number;
  casesWithTrialDate: number;
  headsUps: ExpertDesignationHeadsUpRow[];
}

export default class DiscoveryListsApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  static async overdueAcknowledgments(): Promise<OverdueAcknowledgmentsDto> {
    const response = await fetch(
      `${DiscoveryListsApi.host()}/api/v1/manager/discovery/overdue-acknowledgments`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to load overdue acknowledgments');
    }
    return response.json();
  }

  static async expertDesignationHeadsUp(): Promise<ExpertDesignationHeadsUpDto> {
    const response = await fetch(
      `${DiscoveryListsApi.host()}/api/v1/manager/discovery/expert-designation-heads-up`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to load the expert designation list');
    }
    return response.json();
  }
}
